<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAlert } from '@/libs/alertPortal'
import FlatButton from '@/components/atoms/FlatButton.vue'
import IconClipboard from '@/components/icons/IconClipboard.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'

const props = defineProps<{
  liffAppConfig: {
    liffId: string
    loginRedirectUri: string
  }
}>()

const emit = defineEmits<{
  (event: 'save', value: { url: string }): void
}>()

const alert = useAlert()

const form = ref({
  url: props.liffAppConfig.loginRedirectUri,
})

const errorMessage = ref('')

const validate = () => {
  if (!form.value.url) {
    errorMessage.value = '必須項目です'
    return
  }
  if (!URL.canParse(form.value.url)) {
    errorMessage.value = 'URLが正しくありません'
    return
  }
  errorMessage.value = ''
}

const userURL = computed(
  () => `https://liff.line.me/${props.liffAppConfig.liffId}/login-link`
)

const copyURL = async () => {
  await navigator.clipboard.writeText(userURL.value)

  alert.success('URL をコピーしました')
}

const save = () => {
  validate()
  if (errorMessage.value) return

  emit('save', form.value)
}
</script>

<template>
  <section>
    <section :class="$style.section">
      <p :class="$style.title">自社ログインのURL</p>
      <div :class="$style.uriForm">
        <DolphinTextInput
          :class="[$style.input, $style.uriForm__input]"
          v-model="form.url"
          @blur="validate"
        />
        <FlatButton
          :class="[$style.uriForm__button]"
          variant="primary"
          size="medium"
          @click="save"
          >更新</FlatButton
        >
      </div>
      <p v-if="errorMessage" :class="$style.errorMessage">{{ errorMessage }}</p>
    </section>
    <section
      v-if="props.liffAppConfig.loginRedirectUri"
      :class="$style.section"
    >
      <p :class="$style.title">お客様に送るURL</p>
      <div :class="$style.url">
        <p>{{ userURL }}</p>
        <FlatButton variant="plain" size="small" @click="copyURL">
          <div :class="$style.copy">
            <IconClipboard />
            <span>copy</span>
          </div>
        </FlatButton>
      </div>
    </section>
  </section>
</template>

<style module lang="scss">
.section {
  display: flex;
  flex-direction: column;

  &:nth-child(n + 2) {
    margin-top: 24px;
  }
}

.title {
  font-size: 14px;
  font-weight: 700;
}

.errorMessage {
  color: $color-caution-accent;
  font-size: 12px;
  font-weight: 700;
}

.url {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 8px;
  background-color: $color-ui-background-base;

  &:nth-child(n + 2) {
    margin-top: 8px;
  }
}

.copy {
  display: flex;
  align-items: center;
}

.uriForm {
  display: flex;
  align-items: center;

  &__input {
    flex-grow: 1;
  }

  &__button {
    margin-left: 16px;
  }

  &:nth-child(n + 2) {
    margin-top: 8px;
  }
}
</style>
